import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Typography, Space, Button, Spin, Alert } from 'antd';
import { 
  CarOutlined, 
  UserOutlined, 
  CheckCircleOutlined, 
  ClockCircleOutlined,
  EnvironmentOutlined,
  BarChartOutlined,
  ReloadOutlined,
  SafetyOutlined,
  GlobalOutlined
} from '@ant-design/icons';
import axios from 'axios';
import * as serverconfig from '../../serverconn';
import { NavLink } from 'react-router-dom';

const { Title, Text } = Typography;

const HubManagerIndex = () => {
  console.log('🔍 HubManagerIndex: Component rendering');
  const [loading, setLoading] = useState(true);
  const [hubData, setHubData] = useState({
    hub_name: '',
    hub_logo: '',
    hub_id: null,
    hub_manager_name: ''
  });
  const [statistics, setStatistics] = useState({
    total_deliveries: 0,
    pending_deliveries: 0,
    completed_deliveries: 0,
    online_agents: 0,
    total_health_centers: 0,
    hub_performance: 0
  });

  useEffect(() => {
    fetchHubData();
    fetchStatistics();
  }, []);

  const fetchHubData = () => {
    console.log('🔍 HubManagerIndex: fetchHubData called');
    let token = localStorage.getItem("token");
    if (!token) {
      console.log('🔍 HubManagerIndex: No token found');
      return;
    }

    axios.defaults.headers = {
      "Content-Type": "application/json",
      Authorization: `Token ${token}`,
    };

    axios.get(serverconfig.backendserverurl + "/current_user_profile")
      .then((res) => {
        console.log('🔍 HubManagerIndex: User profile response:', res.data);
        const userProfile = res.data;
        setHubData({
          hub_name: userProfile.hub_name || 'Hub Dashboard',
          hub_logo: userProfile.hub_logo || '',
          hub_id: userProfile.coodinated_hub,
          hub_manager_name: userProfile.first_name + ' ' + userProfile.last_name
        });
      })
      .catch((err) => {
        console.error("Error fetching hub data:", err);
      });
  };

  const fetchStatistics = () => {
    setLoading(true);
    
    // Fetch hub-specific statistics using existing endpoints
    const promises = [
      fetchHubDeliveries(),
      fetchOnlineAgents(),
      fetchHealthCenters()
    ];

    Promise.all(promises)
      .finally(() => {
        setLoading(false);
      });
  };

  const fetchHubDeliveries = () => {
    // Use the existing getumoja_deliveriesreport endpoint with hub filter
    return axios.post(serverconfig.backendserverurl + "/customqueries/getumoja_deliveriesreport", {
      delivery_status: '',
      dateone: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      datetwo: new Date().toISOString().split('T')[0],
      hub: hubData.hub_id
    })
    .then((res) => {
      const deliveries = res.data || [];
      const completed = deliveries.filter(d => d.time_of_dropoff && d.time_of_dropoff !== 'None' && d.time_of_dropoff !== '').length;
      const pending = deliveries.filter(d => !d.time_of_dropoff || d.time_of_dropoff === 'None' || d.time_of_dropoff === '').length;
      
      setStatistics(prev => ({
        ...prev,
        total_deliveries: deliveries.length,
        pending_deliveries: pending,
        completed_deliveries: completed,
        hub_performance: deliveries.length > 0 ? Math.round((completed / deliveries.length) * 100) : 0
      }));
    })
    .catch((err) => {
      console.error("Error fetching deliveries:", err);
    });
  };

  const fetchOnlineAgents = () => {
    // Use the existing delivery agents endpoint with online filter
    return axios.get(serverconfig.backendserverurl + `/api/delivery_agents/?is_online=true&active=true`)
    .then((res) => {
      const allAgents = res.data || [];
      // Filter agents that belong to this hub
      const hubAgents = allAgents.filter(agent => 
        agent.coodinated_hub === hubData.hub_id || 
        agent.hub === hubData.hub_id
      );
      
      setStatistics(prev => ({
        ...prev,
        online_agents: hubAgents.length
      }));
    })
    .catch((err) => {
      console.error("Error fetching online agents:", err);
    });
  };

  const fetchHealthCenters = () => {
    // Use the existing health centers endpoint
    return axios.get(serverconfig.backendserverurl + "/api/healthcentres/")
    .then((res) => {
      const allHealthCenters = res.data || [];
      // Filter health centers that belong to this hub
      const hubHealthCenters = allHealthCenters.filter(hc => 
        hc.coodinated_hub === hubData.hub_id || 
        hc.hub === hubData.hub_id ||
        hc.collection_hub === hubData.hub_id
      );
      
      setStatistics(prev => ({
        ...prev,
        total_health_centers: hubHealthCenters.length
      }));
    })
    .catch((err) => {
      console.error("Error fetching health centers:", err);
    });
  };

  const handleRefresh = () => {
    fetchHubData();
    fetchStatistics();
  };

  if (loading) {
    return (
      <div style={{ padding: '50px', textAlign: 'center' }}>
        <Spin size="large" />
        <div style={{ marginTop: '20px' }}>Loading hub dashboard...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space align="center">
              {hubData.hub_logo && (
                <img 
                  src={hubData.hub_logo} 
                  alt="Hub Logo" 
                  style={{ width: '48px', height: '48px', borderRadius: '8px' }}
                />
              )}
              <div>
                <Title level={2} style={{ margin: 0, color: '#1890ff' }}>
                  {hubData.hub_name}
                </Title>
                <Text type="secondary">
                  Welcome back, {hubData.hub_manager_name}
                </Text>
              </div>
            </Space>
          </Col>
          <Col>
            <Button 
              type="primary" 
              icon={<ReloadOutlined />} 
              onClick={handleRefresh}
            >
              Refresh Data
            </Button>
          </Col>
        </Row>
      </div>

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card>
            <Statistic
              title="Total Deliveries"
              value={statistics.total_deliveries}
              prefix={<CarOutlined style={{ color: '#1890ff' }} />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card>
            <Statistic
              title="Pending Deliveries"
              value={statistics.pending_deliveries}
              prefix={<ClockCircleOutlined style={{ color: '#faad14' }} />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card>
            <Statistic
              title="Completed Deliveries"
              value={statistics.completed_deliveries}
              prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card>
            <Statistic
              title="Online Agents"
              value={statistics.online_agents}
              prefix={<UserOutlined style={{ color: '#722ed1' }} />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card>
            <Statistic
              title="Health Centers"
              value={statistics.total_health_centers}
              prefix={<EnvironmentOutlined style={{ color: '#13c2c2' }} />}
              valueStyle={{ color: '#13c2c2' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card>
            <Statistic
              title="Hub Performance"
              value={statistics.hub_performance}
              suffix="%"
              prefix={<BarChartOutlined style={{ color: '#eb2f96' }} />}
              valueStyle={{ color: '#eb2f96' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Quick Actions */}
      <Card title="Quick Actions" style={{ marginBottom: '24px' }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8}>
            <Button 
              type="primary" 
              size="large" 
              icon={<CarOutlined />}
              style={{ width: '100%', height: '60px' }}
            >
              <NavLink to="/hub_deliveries/" style={{ color: 'white' }}>
                View Deliveries
              </NavLink>
            </Button>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Button 
              size="large" 
              icon={<SafetyOutlined />}
              style={{ width: '100%', height: '60px' }}
            >
              <NavLink to="/hub_online_agents/" style={{ color: 'inherit' }}>
                Online Agents
              </NavLink>
            </Button>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Button 
              size="large" 
              icon={<GlobalOutlined />}
              style={{ width: '100%', height: '60px' }}
            >
              <NavLink to="/hub_agent_activity_map/" style={{ color: 'inherit' }}>
                Activity Map
              </NavLink>
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Recent Activity */}
      <Card title="Recent Activity">
        <Alert
          message="Hub Dashboard Active"
          description="Your hub is currently operational with active agents and deliveries in progress."
          type="success"
          showIcon
        />
      </Card>
    </div>
  );
};

export default HubManagerIndex;
